import type { GoToDemoPreviewArgs } from '@carboncopyco/components';
import { useGetWorkspaceQuery, useLazyGetNotesQuery } from '@carboncopyco/data';
import type { IDemo, IPresentation } from '@carboncopyco/types';
import { TabGroupName, TabType } from '@carboncopyco/types/desktop';
import { PresentationTypeConst } from '@carboncopyco/types/desktop';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { generateGroupId, getGroupDefaultValue } from '../../util/tab-util';
import useOpenDemoTab from '../screens/utils/openDemoTab';
import { usePresentationService } from '../services/presenter/PresentationService';
import { createDemoPreviewSpeakerPanelData } from '../shared/createDemoPreviewSpeakerPanelData';
import { useUser } from '../store/context';
import { useCustomNavigate } from './useCustomNavigate';
import { useFlagsFallback } from './useFlagsFallback';

type TabId = 'presentations' | 'activity' | 'links' | 'overview' | 'history';

type Params = {
  organizationId?: string;
  demoId: string;
  workspaceId?: string;
  tabId?: TabId;
};

export const useDemoCommons = ({ demo }: { demo: IDemo }) => {
  const { trackEvent } = useAnalytics();
  const [getNotes] = useLazyGetNotesQuery();
  const openDemoTab = useOpenDemoTab();
  const [showCreatePresentation, setShowCreatePresentation] = useState(false);
  const { navigateToDemoPreview } = useCustomNavigate();
  const { activeOrganization } = useUser();
  const { openDemoPresentation } = usePresentationService();
  const organizationId = activeOrganization?.id.toString() || '';
  const flags = useFlagsFallback();
  const { demoId, workspaceId } = useParams<Params>();
  const { data: workspace } = useGetWorkspaceQuery(
    {
      organizationId: organizationId || '',
      id: workspaceId || '',
    },
    { skip: !workspaceId }
  );

  const handlePresent = async (presentation: IPresentation) => {
    if (!demo) return;
    const analyticsSessionId = window.dsDesktop.analyticsClient.start({
      presentationId: String(presentation.id),
      presentationType: PresentationTypeConst.demo,
      resourceType: 'managed_presentation',
      forClient: presentation.forClient,
    });
    await openDemoPresentation(presentation, demo, analyticsSessionId);
  };

  const goToDemoPreview = async (options?: GoToDemoPreviewArgs) => {
    if (demo && options) {
      const { presentation } = options;
      const groupId = generateGroupId(
        presentation?.id ? Number(presentation?.id) : demo.id
      );

      const notes = await getNotes({
        organizationId,
        entityId: demo.id.toString(),
        entityType: 'demo',
      }).unwrap();

      const note = presentation?.notes || notes?.[0]?.content;
      await createDemoPreviewSpeakerPanelData(demo, groupId, note);

      openDemoTab(
        {
          ...demo,
          playerToken:
            options?.previewData?.from === PresentationTypeConst.demo &&
            options?.presentation?.playerToken
              ? options.presentation.playerToken
              : demo.playerToken,
          firstUrl: options.presentation?.firstUrl || demo.firstUrl,
          variables: options.presentation?.variables || demo.variables,
        },
        {
          firstUrl: options?.firstUrl,
          tabTitle: demo.demoName,
          tabType: TabType.PREVIEW_DEMO,
          showWithoutDrafts: true,
          forClient: options?.previewData?.client,
          presentationId: options?.presentationId,
          openFromInfoPage: true,
          presentation,
          group: groupId
            ? getGroupDefaultValue(groupId, TabGroupName.DemoPreview)
            : undefined,
        }
      );
      trackEvent(AnalyticsTrackEventName.DEMO_CLICK_PREVIEW, {
        location: 'demo_page',
        demoId: demo.id,
        recordingId: demo.recordId,
        workspaceId: demo.workspaceId,
      });
      return;
    }
    if (!demo) return;
    navigateToDemoPreview(demo);
  };

  const handleCreatePresentation = () => {
    setShowCreatePresentation(true);
  };

  return {
    handlePresent,
    handleCreatePresentation,
    flags,
    demoId,
    workspaceId,
    organizationId,
    workspace,
    setShowCreatePresentation,
    showCreatePresentation,
    goToDemoPreview,
  };
};
